import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_BASE } from "../apiBase";
import { ownerSecretHeader } from "../ownerSecret";

export const CreateWeightBalancePage = () => {
    const navigate = useNavigate();
    const {tail: tailParam} = useParams();
    const [searchParams] = useSearchParams();
    const tailQuery = searchParams.get("tail");

    const rawTail = (tailParam || tailQuery || "").trim().toUpperCase();
    const tailOk = /^[A-Z0-9]+$/.test(rawTail);
    const tail = tailOk ? rawTail : "";

    const [stations, setStations] = useState([
        { id: "Pilot", weight: "" },
        { id: "Copilot", weight: "" },
    ]);
    const [existingRows, setExistingRows] = useState([]);
    const [serverMessage, setServerMessage] = useState("");

    const load = useCallback(async () => {
        if (!tail) {
            setExistingRows([]);
            setServerMessage(`CreateWeightBalancePage: Invalid tail in url: "${tail}" `);
            return;
        }
        try {
            const url = `${API_BASE}/api/wtbals?tail=${encodeURIComponent(tail)}`;
            const res = await fetch(url);
            const text = await res.text();

            if (!res.ok) {
                setExistingRows([]);
                setServerMessage(`GET failed: ${res.status}\n${text}`);
                return;
            }

            const data = JSON.parse(text);
            const list = Array.isArray(data) ? data : (data.wtbals || []);
            setExistingRows(list);
            setServerMessage(`Loaded ${list.length} existing rows.`);
        } catch (e) {
            setServerMessage("GET failed: " + e.toString());
            setExistingRows([]);
        }
    }, [tail]);

    useEffect(() => {
        load();
    }, [load]);

    const handleChange = (index, field, value) => {
        const updated = [...stations];
        updated[index][field] = value;
        setStations(updated);
    };

    const addStationRow = () => {
        setStations([...stations, { id: "", weight: "0" }]);
    };

    const removeStationRow = (index) => {
        setStations((prev) => prev.filter((_, i) => i !== index));
    };

    const norm = (s) => String(s || "").trim().toLowerCase();

    const buildMergedRows = () => {
        const merged = existingRows
            .map((r) => ({ ...r }))
            .filter((r) => {
                const id = String(r?.id ?? "").trim();
                if (!id) return false;
                if (id.toLowerCase() === "limits") return false;
                return true;
            });

        for (const s of stations) {
            const id = String(s.id || "").trim();
            if (!id) continue;

            const wStr = String(s.weight ?? "").trim();
            if (wStr === "") continue;
            const w = Number(wStr);
            if (!Number.isFinite(w)) continue;

            const idx = merged.findIndex((r) => norm(r?.id) === norm(id));
            if (idx >= 0) {
                merged[idx] = { ...merged[idx], id, Weight: w };
            } else {
                merged.push({ id, Weight: w, units: "lbs" });
            }
        }

        return merged;
    };

    const saveAll = async () => {
        if (!tail) {
            setServerMessage("Missing tail (use /wtbals/create/:tail or ?tail=)");
            return;
        }

        const mergedRows = buildMergedRows();

        if (!mergedRows.length) {
            setServerMessage("Nothing to save.");
            return;
        }

        const payload = { tail, wtbals: mergedRows };
        const url = `${API_BASE}/api/wtbals?tail=${encodeURIComponent(tail)}`;

        try {
            const res = await fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    ...ownerSecretHeader(tail),
                },
                body: JSON.stringify(payload)
            });

            const text = await res.text();

            if (!res.ok) {
                setServerMessage(`PUT failed: ${res.status}\n${text}`);
                return;
            }
            setServerMessage("Saved successfully.");
            await load();
            alert("Saved weight & balance updates.");

            navigate(`/wtbals?tail=/${encodeURIComponent(tail)}`, { replace: true });
        } catch (e) {
            setServerMessage("PUT exception: " + String(e));
        }
    };

    return (
        <div style={{ maxWidth: "500px", margin: "0 auto" }}>
            <h3>Add Weight & Balance Stations</h3>
            <p style={{ fontSize: "16px", fontWeight: "bold" }}>Enter values for the weight and balance table</p>
            <p style={{ fontSize: "16px", fontWeight: "bold" }}>DELETE removes row from this table not database</p>
            <div style={{ margin: "8px 0", fontSize: "14px", fontWeight: "bold" }}>
                {serverMessage}
            </div>

            <table id="add-wtbal">
                <thead>
                    <tr>
                        <th>Station Name</th>
                        <th>Weight</th>
                        <th>Action</th>
                    </tr>
                </thead>

                <tbody>
                    {stations.map((station, index) => (
                        <tr key={index}>
                            <td>
                                <input
                                    type="text"
                                    value={station.id}
                                    onChange={(e) => handleChange(index, "id", e.target.value)}
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    value={station.weight}
                                    onChange={(e) => handleChange(index, "weight", e.target.value)}
                                />
                            </td>
                            <td>
                                <div className="wtbal-action">
                                    <button type="button" onClick={() => removeStationRow(index)}>
                                        Delete
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="wtbal-controls">
                <button type="button" onClick={addStationRow}>+ Add Station</button>
                <button type="button" onClick={saveAll}>Save</button>
            </div>
        </div>
    );
};

export default CreateWeightBalancePage;